import React, { useEffect } from 'react';
import BlogLayout from '../../components/BlogLayout';

export default function WhyNotCloud() {
    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    return (
        <BlogLayout
            title="Why Your AI Agent Can't Live in the Cloud"
            date="March 13, 2026"
            readTime="6 MIN"
            heroImage="/images/blog/why-not-cloud-hero.png"
            description="Every time you send a customer's claim to ChatGPT, it goes somewhere. Do you know where? Here's the business case for keeping your AI local."
        >
            <p className="mb-6 text-[#F0EFF4]/80 text-lg leading-relaxed">
                Every time you send a customer's claim to ChatGPT, it goes somewhere. Do you know where?
            </p>

            <p className="mb-6 text-[#F0EFF4]/80 text-lg leading-relaxed">
                Most people don't think about it. They open a browser tab, paste in a homeowner's address, their insurance carrier, the job value, maybe a photo of the damage. They get a useful response. They close the tab.
            </p>

            <p className="mb-12 text-[#F0EFF4]/80 text-lg leading-relaxed">
                What they don't think about: where that data went. Whose servers it's sitting on. Whether their terms of service include using it to train the next version of the model. Whether that homeowner would be comfortable knowing their claim details helped train a commercial AI product.
            </p>

            <p className="mb-12 text-[#F0EFF4]/80 text-lg leading-relaxed">
                That's the cloud assumption at work. Everyone defaults to it because it's convenient. I want to make the case that for roofing companies, convenient is not the same as smart.
            </p>

            <hr className="my-12 border-[#F0EFF4]/10" />

            <h2 className="text-3xl font-heading font-bold mb-6 text-white tracking-tight">The cloud assumption</h2>

            <p className="mb-6 text-[#F0EFF4]/80 text-lg leading-relaxed">
                The cloud assumption has a simple logic: someone else hosts the hard stuff, you pay a subscription, the problem goes away. For a lot of business software, this is fine. Your accounting software lives in the cloud. Your email lives in the cloud. Probably your CRM does too.
            </p>

            <p className="mb-6 text-[#F0EFF4]/80 text-lg leading-relaxed">
                But an AI agent is different.
            </p>

            <p className="mb-6 text-[#F0EFF4]/80 text-lg leading-relaxed">
                An AI agent doesn't just store your data. It thinks with it. It reads your contracts, your estimates, your crew schedules, your customer history. It learns your patterns. The more context it has, the better it performs.
            </p>

            <p className="mb-12 text-[#F0EFF4]/80 text-lg leading-relaxed">
                Which means the data going into your AI agent is the most sensitive data in your business. Not a backup of your invoices sitting inert in someone's database. Live, active, decision-grade information about your customers, your jobs, and your operations.
            </p>

            <p className="mb-12 text-[#F0EFF4]/80 text-lg leading-relaxed">
                When that lives in the cloud, it lives on someone else's terms.
            </p>

            <hr className="my-12 border-[#F0EFF4]/10" />

            <h2 className="text-3xl font-heading font-bold mb-6 text-white tracking-tight">What actually happens to your data</h2>

            <p className="mb-6 text-[#F0EFF4]/80 text-lg leading-relaxed">
                You sign up. You accept the terms. Page 7, section 14, paragraph 3: the vendor may use your data to improve the service. Or: your data may be retained for up to 36 months. Or: by using the API you grant us a license to the inputs.
            </p>

            <p className="mb-6 text-[#F0EFF4]/80 text-lg leading-relaxed">
                None of this is malicious. These companies are not scheming against you. But they are protecting themselves. And their protection means your customers' data is an asset they hold, not you.
            </p>

            <p className="mb-6 text-[#F0EFF4]/80 text-lg leading-relaxed">
                For a roofing company, the specific exposure points are:
            </p>

            <div className="space-y-3 mb-12">
                <div className="bg-[#18181B] border border-[#7B61FF]/20 rounded-xl p-5">
                    <p className="text-[#F0EFF4]/80 text-base leading-relaxed">Client addresses and contact information</p>
                </div>
                <div className="bg-[#18181B] border border-[#7B61FF]/20 rounded-xl p-5">
                    <p className="text-[#F0EFF4]/80 text-base leading-relaxed">Insurance carrier and adjuster details</p>
                </div>
                <div className="bg-[#18181B] border border-[#7B61FF]/20 rounded-xl p-5">
                    <p className="text-[#F0EFF4]/80 text-base leading-relaxed">Job values and negotiated pricing</p>
                </div>
                <div className="bg-[#18181B] border border-[#7B61FF]/20 rounded-xl p-5">
                    <p className="text-[#F0EFF4]/80 text-base leading-relaxed">Damage photos that show exactly what was documented</p>
                </div>
                <div className="bg-[#18181B] border border-[#7B61FF]/20 rounded-xl p-5">
                    <p className="text-[#F0EFF4]/80 text-base leading-relaxed">Internal notes on claims history and customer relationships</p>
                </div>
            </div>

            <p className="mb-12 text-[#F0EFF4]/80 text-lg leading-relaxed">
                None of that is generic business information. It is the kind of thing that, if it ended up in the wrong place, would cost you a client relationship. Possibly more.
            </p>

            <hr className="my-12 border-[#F0EFF4]/10" />

            <h2 className="text-3xl font-heading font-bold mb-6 text-white tracking-tight">The trust gap</h2>

            <p className="mb-6 text-[#F0EFF4]/80 text-lg leading-relaxed">
                I am not saying cloud AI is a disaster waiting to happen. I am saying: do you know whose rules you are operating under?
            </p>

            <p className="mb-6 text-[#F0EFF4]/80 text-lg leading-relaxed">
                Most contractors who use cloud AI tools haven't read the terms. They assume the vendor is trustworthy because the product works well. That is usually fine. Until it isn't.
            </p>

            <p className="mb-6 text-[#F0EFF4]/80 text-lg leading-relaxed">
                The roofing industry runs on trust. A homeowner lets you onto their property, into their attic, and in front of their insurance adjuster. They trust you with a lot. They did not sign a cloud AI agreement. You did.
            </p>

            <p className="mb-12 text-[#F0EFF4]/80 text-lg leading-relaxed">
                The simplest version of data stewardship is this: keep your clients' information on your own hardware.
            </p>

            <hr className="my-12 border-[#F0EFF4]/10" />

            <h2 className="text-3xl font-heading font-bold mb-6 text-white tracking-tight">What local-first looks like</h2>

            <p className="mb-6 text-[#F0EFF4]/80 text-lg leading-relaxed">
                A RoofClaw system runs on a Mac Mini in your office. Or in your truck bay. The AI models run locally. The data never leaves your building unless you choose to send it somewhere. Your customer records, your job history, your integrations with ProLine or Roofr or whatever CRM you use. All of it stays on hardware you own.
            </p>

            <p className="mb-6 text-[#F0EFF4]/80 text-lg leading-relaxed">
                When you need to look something up, you look it up. The data doesn't go to a third party for processing. The model is there. Your data is there. The answer comes back.
            </p>

            <p className="mb-6 text-[#F0EFF4]/80 text-lg leading-relaxed">
                It is also faster. There is no API call to a server in Virginia. No rate limit at 11 PM when you are reviewing an estimate. No billing spike in April when hail season hits and your query volume triples.
            </p>

            <p className="mb-12 text-[#F0EFF4]/80 text-lg leading-relaxed">
                That last point matters more than people think.
            </p>

            <hr className="my-12 border-[#F0EFF4]/10" />

            <h2 className="text-3xl font-heading font-bold mb-6 text-white tracking-tight">The business math</h2>

            <p className="mb-6 text-[#F0EFF4]/80 text-lg leading-relaxed">
                A per-seat SaaS model at $50/month feels manageable for a two-person office. Add a crew lead. Add an admin. Add the AI credits you burned through during storm season. That $50 becomes $300, then $500.
            </p>

            <p className="mb-6 text-[#F0EFF4]/80 text-lg leading-relaxed">
                Local AI has a hardware cost upfront and minimal ongoing cost. No per-query billing. No tier upgrades when your usage grows. The model runs whether you use it once today or a thousand times. Same cost.
            </p>

            <p className="mb-12 text-[#F0EFF4]/80 text-lg leading-relaxed">
                The contractors I have talked to who made the switch describe it the same way: they stopped thinking about the meter. They stopped rationing queries because each one costs something. They just used it. That behavioral shift is worth more than the dollar savings.
            </p>

            <hr className="my-12 border-[#F0EFF4]/10" />

            <p className="mb-6 text-[#F0EFF4]/80 text-xl leading-relaxed font-medium">
                You do not rent your crew. You hire them, train them, and they work for you.
            </p>

            <p className="mb-6 text-[#F0EFF4]/80 text-lg leading-relaxed">
                The smart version of AI for a roofing business works the same way. It runs on your hardware. It knows your data. It works on your schedule. Nobody else's terms apply.
            </p>

            <p className="mb-6 text-[#F0EFF4]/80 text-lg leading-relaxed">
                That is not paranoia. That is ownership.
            </p>
        </BlogLayout>
    );
}
